/**
 * Spec 163 — may this user use the Security area?
 *
 * cb-api refuses every /security route to anyone Centerbase does not call an
 * administrator (`cb-api/src/domains/security/adminGate.js`): a **403 with
 * `code: 'not-admin'`** when CB answered no, a **503 `admin-check-unavailable`** when
 * CB could not answer. The Security page asks `GET /security/access` first and reads
 * nothing else unless the answer is an explicit yes.
 *
 * A dead CB session is a **401** instead, which never reaches this module in the app:
 * `client.js`'s response interceptor refreshes the token once and re-asks, then forces
 * a clean re-login, holding the query in flight meanwhile (`checking`). Only a client
 * without that interceptor would see the 401 here, and it reads as `unconfirmed`.
 *
 * Three outcomes must never look alike here, for the same reason an empty list and a
 * failed read must not (`readState.js`): "you are not an administrator" is a claim
 * about the reader, so it may only be made when CB made it. Any other failure (a 503,
 * a network error, a 403 some proxy produced without CB's code, a 200 whose body is
 * not an explicit yes) is `unconfirmed`, and the page says that nobody found out.
 */

/** Must equal cb-api's NOT_ADMIN_CODE (pinned by securityAccess.test.js). */
export const NOT_ADMIN_CODE = 'not-admin'

/** True only for CB's coded refusal. */
export const isNotAdminRefusal = (error) =>
  error?.response?.status === 403 && error?.response?.data?.code === NOT_ADMIN_CODE

/**
 * @param {'failed'|'blocked'|'idle'|'loading'|'ready'} state  `readState` / `useReadState` of the access query
 * @param {object} query  the access query (`useQuery` result)
 * @returns {'checking'|'admin'|'not-admin'|'unconfirmed'|'blocked'}
 */
export const securityAccessFrom = (state, query) => {
  if (state === 'failed') return isNotAdminRefusal(query?.error) ? 'not-admin' : 'unconfirmed'
  if (state === 'blocked') return 'blocked'
  if (state === 'ready') return query?.data?.data?.isAdmin === true ? 'admin' : 'unconfirmed'
  return 'checking'
}
